import { LoadingOverlay, Title } from '@mantine/core';
import Breadcrumbs, { BreadcrumbItem } from 'components/BreadcrumbsComponent';
import CreateSchoolForm from 'components/forms/dacum/CreateSchoolForm';
import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { pathnames } from 'routes/dacum';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { schoolService } from 'services/dacum/school.service';
import { showSuccessNotification, showErrorNotification } from 'utils/notification';
import { useSchoolManagementStore } from 'store/schoolManagement';
import { SchoolDataForm } from 'types/dacum/schoolManagement';

const CreateSchoolPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams();
  const { formData, mode, setFormData, setMode } = useSchoolManagementStore();

  const { data: schoolDetail, isLoading } = useQuery({
    queryKey: ['schoolDetail', id],
    queryFn: () => schoolService.detailSchool(id as string),
    enabled: !!id
  });

  useEffect(() => {
    if (id && schoolDetail) {
      setMode('edit');
      let _listJobFamily = [];
      if (schoolDetail?.data?.jobFamilies && schoolDetail?.data?.jobFamilies.length > 0) {
        _listJobFamily = schoolDetail.data.jobFamilies.map(
          (item: { jobFamilyID: string }) => item.jobFamilyID
        );
      }
      setFormData({
        schoolName: schoolDetail.data.schoolName,
        description: schoolDetail.data.description,
        notes: schoolDetail.data.notes,
        businessUnitID: schoolDetail.data.businessUnitID,
        businessUnit: schoolDetail.data.businessUnitID,
        msSchoolCompetencyManagementID: schoolDetail.data.msSchoolCompetencyManagementID,
        jobFamilyGroupID: schoolDetail.data.jobFamilyGroupID || '',
        listJobFamily: _listJobFamily || []
      });
    }
  }, [id, schoolDetail, setMode]);

  const breadcrumbs = [
    { title: 'School Management', href: pathnames.schoolManagement },
    { title: mode === 'add' ? 'New' : 'Edit', href: '' }
  ];

  const createSchool = useMutation({
    mutationFn: (data: SchoolDataForm) => schoolService.createSchool(data),
    onSuccess: () => {
      showSuccessNotification({
        title: 'School Created',
        message: 'School has been created successfully.'
      });
      queryClient.invalidateQueries({ queryKey: ['schoolCompetency'] });
      navigate(pathnames.schoolManagement);
    },
    onError: () => {
      showErrorNotification({
        title: 'Failed to create school',
        message: 'Failed to create school, please try again later.'
      });
    }
  });

  const updateSchool = useMutation({
    mutationFn: (data: SchoolDataForm) => {
      return schoolService.updateSchool({
        ...data,
        msSchoolCompetencyManagementID: formData.msSchoolCompetencyManagementID as string
      });
    },
    onSuccess: () => {
      showSuccessNotification({
        title: 'School Updated',
        message: 'School has been updated successfully.'
      });
      queryClient.invalidateQueries({ queryKey: ['schoolCompetency'] });
      navigate(pathnames.schoolManagement);
    },
    onError: () => {
      showErrorNotification({
        title: 'Failed to update school',
        message: 'Failed to update school, please try again later.'
      });
    }
  });

  return (
    <>
      <Breadcrumbs items={breadcrumbs as BreadcrumbItem[]} mb={32} />
      <Title order={2} mb={30}>
        {mode === 'add' ? 'Create New' : 'Edit'} School
      </Title>
      <CreateSchoolForm createSchool={createSchool} updateSchool={updateSchool} />
      <LoadingOverlay visible={isLoading} />
    </>
  );
};

export default CreateSchoolPage;
